<template>
	<section class="marketing-section bg-color-bg-tertiary pt-400-smo">
		<MarketingPageHeader :header="sectionHeader" :is-secondary="false" :has-outer-spacing="false" />
		<div class="container">
			<hr v-if="appStore.isMobile" class="divider my-300" />

			<div class="mt-500 mt-300-smo grid display-flex-smo flex-direction-column-smo">
				<div class="col-5-md pr-600-md">
					<h3 class="heading-2">What is private credit?</h3>
					<p>
						Private credit (or private lending) is an asset class that consists generally of loans,
						fixed-income, or other structured investments that aim to deliver higher yields with lower
						overall risk when compared to equity investments. In other words, investors in private credit
						are lending money to borrowers in exchange for a fixed rate of return (often captured in the
						form of an interest rate or preferred return) but typically do not have any equity ownership or
						upside participation. Similar to other private market assets, private credit differs from
						publicly traded credit or fixed-income investments, such as bonds or asset-backed securities,
						because it is illiquid and consequently aims to deliver a higher relative return.
					</p>
					<div v-if="appStore.isMobile" class="mt-300">
						<img :src="mobileChartImgUrl" alt="Inverted Yield Curve chart" />
						<p class="body-xs text-color-content-tertiary"
							><strong>Source: </strong> {{ imageSourceDescription }}</p
						>
					</div>
					<h3 class="mt-200 mt-300-smo mb-100 heading-2">The profitability of preparation</h3>
					<div>
						<p>
							As a result of the Fed's ongoing policies, it is still nearly as expensive to borrow money
							for 30 days as for 30 years. This continued flattened yield curve is an unnatural state of
							affairs that has led to an ongoing liquidity crunch that we call
							<BaseLink
								classes="text-decoration-underline"
								:link="{
									router: 'blog-post',
									params: { blogPostUrlSlug: 'onward-episode-20' },
									target: '_blank'
								}"
								>The Great Deleveraging</BaseLink
							>.</p
						><p>
							During this period, many individuals and companies have been forced to borrow money,
							especially in the near term, at significantly more favorable terms for investors. Higher
							interest rates generally means that borrowers are borrowing at much lower leverage (which
							means lower risk). Loans reaching maturity during this cycle often require substantial
							paydowns, and for new loans the gap between the expected and actual proceeds means many
							borrowers are resorting to the use of “bridge” or “mezzanine” financing.</p
						>
						<p
							>This has created an opportunity for investors who have been diligent and chosen to maintain
							larger cash positions during these times, putting them in the enviable position of being
							able to demand significantly more return in exchange for providing liquidity during what we
							expect is likely to be a temporary (although longer than initially expected) period of
							realignment.</p
						>
						<p
							>At Fundrise, we aim to take advantage of this dynamic for our investors to drive stronger
							returns during what remains a highly uncertain environment.</p
						>
						<p>
							<strong>Important Note:</strong> In our experience, these types of unique investing
							environments are short-lived. Accordingly, our expectation is that the current period of
							disruption is likely to close as rates come down.</p
						>
					</div>
				</div>
				<div class="col-7-md position-sticky-md">
					<div v-if="!appStore.isMobile">
						<img :src="chartImgUrl" alt="Inverted Yield Curve chart" />
						<p class="body-xs text-color-content-tertiary"
							><strong>Source: </strong> {{ imageSourceDescription }}</p
						>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { appCloudfrontPath } from '@constants';
import { LandingPageHeader } from 'types/layout';
import MarketingPageHeader from '@components/marketing-site/marketing-page-header.vue';
import { useAppStore } from '@stores/app';

const appStore = useAppStore();

const imageSourceDescription =
	'Historical Treasury rates sourced from St. Louis Federal Reserve Economic Data (FRED). Forecast based on FOMC projections for the Fed Funds rate and Fundrise internal analysis of Treasury spread behavior from historical yield curve inversion events.';
const chartImgUrl = `${appCloudfrontPath}/images/marketing-site/inverted-yield-curve-desktop.png?v=26`;
const mobileChartImgUrl = `${appCloudfrontPath}/images/marketing-site/inverted-yield-curve-mobile.png?v=26`;

const sectionHeader: LandingPageHeader = {
	title: 'Why Private Credit',
	heading: 'A once-in-a-decade lending environment',
	summary: [
		`The Federal Reserve has maintained higher interest rates after having raised rates at unprecedented speed in an effort to control inflation. This initial aggressive policy shift led to destabilization in many credit markets, resulting in widespread dislocations, systemic strain, and the emergence of potential liquidity risks that continue today.`,
		`These conditions have created what we believe is one of the most compelling environments for credit investments in the past several decades.`
	]
};
</script>

<style lang="scss" scoped>
@use '../../../styles/constants/_colors.scss' as *;

.divider {
	border: 0;
	border-top: 1px solid gray(20);
}

.position-sticky-md {
	top: 200px;
}
</style>
